import type { FC } from 'react';
import React, { memo } from 'react';
import { defineMessages } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { buildCreationLink } from '@confluence/create-blank-fabric-page/entry-points/createContentRouteUtils.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { UNIVERSAL_BLOG_SHORTCUT } from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { CREATE_PAGETREE_BLOG_BUTTON_CLICK } from '@confluence/edit-common/entry-points/event-names.ts';
import { CreateContentButtonPreloader } from '@confluence/edit-button/entry-points/CreateContentButtonPreloader.ts';
import type { SpaceOperationsQueryType } from '@confluence/entity-operations/entry-points/SpaceOperationsQuery.ts';

import { CreateButtonBase } from './CreateButtonBase.tsx';

const i18n = defineMessages({
	label: {
		id: 'contextual-create.create-blog.label',
		description: 'Label for button that creates a blog in the current space',
		defaultMessage: 'Create a blog',
	},
});

export type Props = {
	spaceKey: string;
};

const CreateBlogButtonComponent: FC<Props> = memo(({ spaceKey }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireCreationAnalytics = ({ spaceId }) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'contextualCreateButton',
				actionSubjectId: 'blogTree',
				source: 'containerNavigation',
				containerType: 'space',
				containerId: spaceId,
				attributes: {
					navdexPointType: START_TOUCH,
				},
			},
		}).fire();
	};

	const createBlogHref = buildCreationLink({
		contentType: 'blog',
		source: 'spaceLevelBlogContextualCreate',
		spaceKey,
	});

	return (
		<CreateContentButtonPreloader editSource={CREATE_PAGETREE_BLOG_BUTTON_CLICK}>
			<CreateButtonBase
				canCreateContent={canCreateBlog}
				onCreateContent={fireCreationAnalytics}
				href={createBlogHref}
				labelI18n={i18n.label}
				tooltipShortcut={UNIVERSAL_BLOG_SHORTCUT}
				spaceKey={spaceKey}
				tooltipPosition="top"
			/>
		</CreateContentButtonPreloader>
	);
});

export const CreateBlogButton: FC<Props> = withTransparentErrorBoundary({
	attribution: Attribution.PAGE_MODES,
})(CreateBlogButtonComponent);

const canPerform = (data: SpaceOperationsQueryType, operation: string, targetType: string) => {
	const operations = data?.space?.operations ?? [];
	return operations.some((op) => op?.operation === operation && op?.targetType === targetType);
};

const canCreateBlog = (data: SpaceOperationsQueryType) => canPerform(data, 'create', 'blogpost');
